import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { useActor } from "@xstate/react";
import { createContext, ReactElement, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { ActorLogicFrom, ActorRefFrom } from "xstate";
import { checkoutMachine } from "./CheckoutMachine";

import { getAllCarts } from "@/redux/selectors";
import _get from "lodash/get";
import _isEqual from "lodash/isEqual";

type CheckoutContextType = {
  send: ActorRefFrom<ActorLogicFrom<typeof checkoutMachine>>["send"];
  actorRef: ActorRefFrom<typeof checkoutMachine>;
};

export const DEFAULT_SECTION_STATE = {
  account: {
    open: false,
    chevronShow: true,
    errorMessage: "",
  },
  payment: {
    open: false,
    chevronShow: true,
    errorMessage: "",
  },
  shipping: {
    open: false,
    chevronShow: true,
    errorMessage: "",
  },
  delivery: {
    open: false,
    chevronShow: true,
    errorMessage: "",
  },
};

export const CheckoutContext = createContext<CheckoutContextType | null>(null);

export const CheckoutMachineProvider = ({
  children,
  orderId,
}: {
  children: ReactElement | ReactElement[];
  orderId: string;
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const allCarts = useAppSelector(getAllCarts, _isEqual);
  const retailerIds = allCarts.map((cart) => cart.retailerId) as number[];
  const retailerConnectionInfoList = useAppSelector(
    (state) =>
      state.retailers.map((retailer) => {
        const status = _get(state.userProfile, [
          "extendedAttributes",
          `connectedRetailer_${retailer.parentRetailerId}_state`,
        ]);
        return {
          retailerId: retailer.parentRetailerId,
          credential: {
            connectionStatus: status,
          },
        };
      }),
    _isEqual
  );

  const actor = useActor(checkoutMachine, {
    input: {
      context: {
        products: {},
        sectionState: DEFAULT_SECTION_STATE,
        checkoutAllCarts: allCarts,
        navigate,
        dispatch,
        retailerIds,
        retailerConnectionInfoList,
        activeCheckoutRetailerId: retailerIds[0],
        orderId,
      },
    },
  });

  const [, send, actorRef] = actor;

  return (
    <CheckoutContext.Provider
      value={{
        send,
        actorRef,
      }}
    >
      {children}
    </CheckoutContext.Provider>
  );
};

export const useCheckoutContext = () => {
  const checkoutContext = useContext(CheckoutContext);
  if (!checkoutContext) {
    throw new Error(
      "useCheckoutContext must be used within a CheckoutProvider"
    );
  }
  return checkoutContext;
};
